import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  connectReadwise,
  countReadwiseHighlights,
  disconnectReadwise,
  fetchIntegration,
  fetchReadwiseCategories,
  getReadwiseIntegrationMeta,
  purgeReadwise,
  ReadwiseIntegration,
  syncReadwise,
  testReadwiseConnection,
  updateReadwiseCategories,
} from "@/lib/readwise";

export type PurgeMode = "supplementals" | "tweets" | "all";

export function useReadwiseIntegration(workspaceId: string | null, personaId: string | null) {
  const [integration, setIntegration] = useState<ReadwiseIntegration | null>(null);
  const [highlightCount, setHighlightCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [savingCats, setSavingCats] = useState(false);
  const [purging, setPurging] = useState(false);

  const refresh = useCallback(async () => {
    if (!workspaceId || !personaId) return;
    setLoading(true);
    try {
      const [integ, count] = await Promise.all([
        fetchIntegration(personaId),
        countReadwiseHighlights(workspaceId),
      ]);
      setIntegration(integ);
      setHighlightCount(count);
      if (integ) {
        try {
          setCategories(await fetchReadwiseCategories(personaId));
        } catch (e) {
          console.error(e);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, personaId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const connect = async (token: string): Promise<boolean> => {
    if (!personaId) {
      toast.error("No active Persona");
      return false;
    }
    setBusy(true);
    try {
      const test = await testReadwiseConnection(token);
      if (!test.ok) {
        toast.error("Readwise rejected this token. Double-check it and try again.");
        return false;
      }
      await connectReadwise(personaId, token);
      toast.success("Connected to Readwise. Starting first sync…");
      await refresh();
      if (workspaceId) {
        setSyncing(true);
        try {
          const r = await syncReadwise(workspaceId, personaId);
          toast.success(`Imported ${r.imported} highlights`);
          await refresh();
        } catch (e) {
          toast.error((e as Error).message);
        } finally {
          setSyncing(false);
        }
      }
      return true;
    } catch (e) {
      toast.error((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    if (!workspaceId || !personaId) return;
    setSyncing(true);
    try {
      const r = await syncReadwise(workspaceId, personaId);
      toast.success(`Synced ${r.imported} highlights`);
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!personaId) return;
    if (!confirm("Disconnect Readwise? Imported highlights will remain in your workspace.")) return;
    setBusy(true);
    try {
      await disconnectReadwise(personaId);
      toast.success("Disconnected");
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleCategory = async (key: string) => {
    if (!personaId) return;
    const next = categories.includes(key)
      ? categories.filter((c) => c !== key)
      : [...categories, key];
    if (next.length === 0) {
      toast.error("Pick at least one category to sync.");
      return;
    }
    const prev = categories;
    setCategories(next);
    setSavingCats(true);
    try {
      await updateReadwiseCategories(personaId, next);
    } catch (e) {
      setCategories(prev);
      toast.error((e as Error).message);
    } finally {
      setSavingCats(false);
    }
  };

  const purge = async (mode: PurgeMode) => {
    if (!workspaceId) return;
    setPurging(true);
    try {
      const r = await purgeReadwise(workspaceId, mode);
      toast.success(`Deleted ${r.deleted.toLocaleString()} highlights`);
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPurging(false);
    }
  };

  return {
    integration,
    integrationMeta: getReadwiseIntegrationMeta(integration),
    highlightCount,
    loading,
    busy,
    syncing,
    categories,
    savingCats,
    purging,
    refresh,
    connect,
    sync,
    disconnect,
    toggleCategory,
    purge,
  };
}
